import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PageBanner from "../Common/PageBanner";
import ScrollReveal from "../Common/ScrollReveal";
import LoanCalculator from "../Landing/LoanCalculator";
import "./OurProducts.css";

/* Black-shade accent for financial theme */
const accent = {
  bg: "bg-[#f3f4f6]",
  icon: "#1f2937",
  border: "group-hover:border-[#374151]/40",
  bar: "bg-[#1f2937]",
};

const ProductIcon = ({ name }) => {
  const stroke = accent.icon;
  const svgProps = { width: 36, height: 36, viewBox: "0 0 40 40", fill: "none", xmlns: "http://www.w3.org/2000/svg", className: "flex-shrink-0" };
  const icons = {
    housing: (
      <svg {...svgProps}>
        <path d="M20 4L6 16h3v20h8V26h6v10h8V16h3L20 4z" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    personal: (
      <svg {...svgProps}>
        <circle cx="20" cy="14" r="6" stroke={stroke} strokeWidth={2} />
        <path d="M10 34c0-5.5 4.5-10 10-10s10 4.5 10 10" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
      </svg>
    ),
    gold: (
      <svg {...svgProps}>
        <path d="M20 6v28M13 17l7-5 7 5v11H13V17z" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 11l-4 3v7h8v-7l-4-3z" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    property: (
      <svg {...svgProps}>
        <rect x="6" y="16" width="28" height="18" rx="2" stroke={stroke} strokeWidth={2} />
        <path d="M14 16V11a4 4 0 018 0v5" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
      </svg>
    ),
    corporate: (
      <svg {...svgProps}>
        <rect x="7" y="10" width="26" height="20" rx="3" stroke={stroke} strokeWidth={2} />
        <path d="M12 18h16M12 22h10" stroke={stroke} strokeWidth={2} strokeLinecap="round" />
      </svg>
    ),
    securities: (
      <svg {...svgProps}>
        <path d="M20 8v24M10 20l10-8 10 8M10 28l10 8 10-8" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    sme: (
      <svg {...svgProps}>
        <circle cx="20" cy="20" r="16" stroke={stroke} strokeWidth={2} />
        <path d="M20 12v8l5 6" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  };
  return icons[name] ?? null;
};

const products = [
  { id: "personal-loan", title: "Personal Loans", description: "Our personal loans are designed to meet a wide range of individual financial needs, including medical expenses, education, travel, weddings, and other personal requirements. With minimal documentation, flexible repayment options, and transparent pricing, we ensure a quick and hassle-free borrowing experience.", icon: "personal" },
  { id: "housing-loan", title: "Housing Loans", description: "Our housing loans help individuals and families fulfill their dream of owning a home. We offer financing for the purchase, construction, or renovation of residential properties, with competitive interest rates, longer tenures, and clear terms to make home ownership accessible and affordable.", icon: "housing" },
  { id: "short-term-loans", title: "Short Term Loans", description: "Our short-term loans provide quick liquidity to address immediate financial requirements. These loans are ideal for managing temporary cash flow gaps and unforeseen expenses, offering faster disbursement, shorter tenures, and straightforward repayment structures.", icon: "securities" },
  { id: "business-finance", title: "Business Financing Solutions", description: "We provide customized business financing solutions to support entrepreneurs, MSMEs, and enterprises in meeting their operational and growth needs. Our solutions are structured to assist with working capital, expansion, asset acquisition, and day-to-day business requirements.", icon: "corporate" },
  { id: "loan-against-property", title: "Loan Against Property (LAP)", description: "Our Loan Against Property solutions enable individuals and businesses to unlock the value of their owned residential or commercial property. LAP offers higher loan amounts at competitive interest rates, making it suitable for business expansion, debt consolidation, or long-term financial planning.", icon: "property" },
  { id: "business-loans", title: "Business Loans", description: "Our business loans are designed to help enterprises grow and scale efficiently. Whether it is for working capital, equipment purchase, or business expansion, we offer flexible financing options with transparent terms and a customer-centric approach.", icon: "sme" },
];

const OurProducts = () => {
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (hash) {
      const el = document.getElementById(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, []);

  return (
  <div className="our-products-page min-h-screen bg-[#f8f9fa] text-[#1a1a2e]">
    <PageBanner title="Our Products" />

    <main className="mx-auto max-w-[1120px] px-6 pb-16 md:px-4 md:pb-12">
      <ScrollReveal variant="fadeUp">
        <section className="py-12 pb-10 md:py-10 md:pb-8">
          <h2 className="products-section-heading mb-4 font-semibold uppercase tracking-wider text-[#4b5563]">
            What we offer
          </h2>
          <div className="space-y-4">
            <p className="products-body-text text-[#374151]">
              At Pawansut Holdings Limited, we offer a comprehensive range of financial products designed to meet the diverse needs of individuals and businesses. Our offerings include personal loans and business financing solutions that support personal aspirations, working capital requirements, and business growth. We are committed to delivering financial services that are accessible, transparent, and customer-centric, supported by efficient processes and responsible lending practices.
            </p>
            <p className="products-body-text text-[#374151]">
              Backed by decades of experience and a strong commitment to financial inclusion, our products are designed to help customers achieve their financial goals with confidence, clarity, and trust, while creating sustainable value for all stakeholders.
            </p>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal variant="fadeUp" delay={0.1}>
        <section className="border-t border-[#e5e7eb] pt-10 pb-14 md:pt-8 md:pb-10">
          <h2 className="products-section-heading mb-6 font-semibold uppercase tracking-wider text-[#4b5563]">
            Explore our products
          </h2>
          <div className="products-grid grid grid-cols-1 gap-6 sm:grid-cols-2">
            {products.map((product) => (
              <div
                key={product.id}
                id={product.id}
                className={`products-card group relative flex overflow-hidden rounded-xl border border-[#e5e7eb] bg-white transition-all duration-200 hover:border-[#374151]/30 hover:shadow-md ${accent.border} scroll-mt-24`}
              >
                <div className={`absolute top-0 left-0 w-1 h-full ${accent.bar} opacity-0 group-hover:opacity-100 transition-opacity duration-200`} />
                <div className="flex flex-1 gap-5 p-5 pl-6 min-w-0">
                  <div className={`products-card-icon flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${accent.bg}`}>
                    <ProductIcon name={product.icon} />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <h3 className="products-card-title mb-2 font-semibold leading-snug text-[#111827]">
                      {product.title}
                    </h3>
                    <p className="products-card-desc mb-3 flex-1 text-[#4b5563] leading-relaxed">
                      {product.description}
                    </p>
                    <Link
                      to="/contact"
                      className="products-card-cta inline-flex items-center gap-1.5 text-sm font-medium text-[#1f2937] transition-colors hover:text-[#111827]"
                    >
                      Enquire now
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </ScrollReveal>
    </main>

    <ScrollReveal variant="fadeUp">
      <section className="border-t border-[#e5e7eb]">
        <LoanCalculator />
      </section>
    </ScrollReveal>
  </div>
  );
};

export default OurProducts;
